import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { PlusIcon, PencilSimpleIcon, TrashIcon, ArrowLeftIcon } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';

import { Card } from '@/components/ui/card';
import CustomButton from '@/components/inputs/CustomButton';
import useLoading from '@/components/loading/hooks/useLoading';
import useSnackbar from '@/components/dialogs/snackbars/hooks/useSnackbar';
import useConfirm from '@/components/dialogs/hooks/useConfirm';
import Connections, { ApiEndpoints } from '@/components/connections/Connections';

const iconBtn = 'inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer';

const CustomerList = ({ companyId: companyIdProp, embedded = false }) => {
  const params = useParams();
  const companyId = companyIdProp || params.companyId;
  const [customers, setCustomers] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, Loading, setLoading } = useLoading(true);
  const { createSuccessSnackbar, createErrorSnackbar } = useSnackbar();
  const { openConfirm, ConfirmDialog } = useConfirm();

  const loadCustomers = async () => {
    const res = await Connections.postRequest(ApiEndpoints.customersAll, { companyId });
    if (res.ok) {
      setCustomers(res.data.customers || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCustomers().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleDelete = async customerId => {
    const confirmed = await openConfirm(t('customer.deleteConfirm'));
    if (!confirmed) return;

    setLoading(true);
    const res = await Connections.postRequest(ApiEndpoints.customerDelete, { companyId, customerId });
    if (res.ok) {
      createSuccessSnackbar(t('customer.deleted'));
      await loadCustomers();
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  if (loading) return <Loading />;

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>{t('app.title')} | {t('customers')}</title>
        </Helmet>
      )}
      <ConfirmDialog />
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          {!embedded && (
            <button type="button" className={iconBtn} onClick={() => navigate('/companies')}>
              <ArrowLeftIcon size={20} />
            </button>
          )}
          <h1 className="text-2xl font-semibold">{t('customers')}</h1>
        </div>
        <CustomButton onClick={() => navigate(`/companies/${companyId}/customers/new`)}>
          <PlusIcon size={18} />
          {t('customer.create')}
        </CustomButton>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t('name')}</th>
              <th className="px-4 py-3 font-medium">{t('eik')}</th>
              <th className="px-4 py-3 font-medium">{t('vatId')}</th>
              <th className="px-4 py-3 font-medium">{t('email')}</th>
              <th className="px-4 py-3 font-medium">{t('referenceNumber')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(customer => (
              <tr key={customer._id} className="border-t hover:bg-muted/30">
                <td className="px-4 py-3">{customer.name}</td>
                <td className="px-4 py-3">{customer.eik}</td>
                <td className="px-4 py-3">{customer.vatId}</td>
                <td className="px-4 py-3">{customer.email}</td>
                <td className="px-4 py-3">{customer.referenceNumber}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      className={iconBtn}
                      onClick={() => navigate(`/companies/${companyId}/customers/${customer._id}/edit`)}
                    >
                      <PencilSimpleIcon size={18} />
                    </button>
                    <button
                      type="button"
                      className={`${iconBtn} hover:text-destructive`}
                      onClick={() => handleDelete(customer._id)}
                    >
                      <TrashIcon size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  {t('customer.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </>
  );
};

export default CustomerList;
